import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@progress/kendo-react-buttons";
import { Typography } from "@progress/kendo-react-common";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import {
  min2CharValidator,
  requiredValidator,
} from "../../components/formFields/CommonValidator";
import ShadowCard from "../../components/common/ShadowCard";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import FormTextField from "../../components/formFields/FormTextField";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { STATUSARRAY } from "../../_contstants/common";
import FormImageUpload from "../../components/formFields/FormImageUpload";
import {
  ActivityImageUpload,
  createActivity,
  getActivityByID,
  updateActivity,
} from "./services/activities.services";
import { clearActivityDetails } from "./activitiesSlice";
import FormRichTextField from "../../components/formFields/FormRichTextField";
import { getFile, slugify } from "../../_helper/helper";
import { getAllActiveProperties } from "../property/services/property.services";
import { IProperty } from "../property/propertyModel";
import FormNumericTextField from "../../components/formFields/FormNumericTextField";

const CreateActivity: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const ActivityID = location.state?.ActivityID;
  const ActivityDetail = useAppSelector(
    (state) => state.activity.ActivityDetail,
  );
  const loading = useAppSelector((state) => state.activity.loading);
  const PropertyList = useAppSelector((state) => state.property.PropertyList);
  const [formKey, setFormKey] = useState(1);

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [ActivityDetail]);

  useEffect(() => {
    dispatch(getAllActiveProperties());
    if (ActivityID) {
      dispatch(getActivityByID(ActivityID));
    }
    return () => {
      dispatch(clearActivityDetails());
    };
  }, [ActivityID]);

  const handleSubmit = async (values: any) => {
    const payload = {
      PropertyID: values?.PropertyID ? +values?.PropertyID : null,
      Title: values?.Title ? values?.Title : "",
      Description: values?.Description ? values?.Description : "",
      SequenceNo: values?.SequenceNo ? +values?.SequenceNo : null,
      IsActive: values.IsActive === 1,
    };
    const ImageData = new FormData();
    const Image =
      values?.Image && typeof values?.Image !== "string"
        ? getFile(values?.Image)
        : "";

    try {
      if (ActivityID) {
        const editPayload = {
          ...payload,
          ID: ActivityID,
        };

        const response = await dispatch(updateActivity(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          if (values?.Image && typeof values?.Image !== "string") {
            ImageData.append("Image", Image);
            ImageData.append("ID", ActivityID);

            await dispatch(ActivityImageUpload(ImageData));
          }
          navigate("/activities");
          dispatch(clearActivityDetails());
        }
      } else {
        const response = await dispatch(createActivity(payload));
        if (response?.meta?.requestStatus === "fulfilled") {
          if (
            values?.Image &&
            typeof values?.Image !== "string" &&
            response?.payload?.Data?.ID
          ) {
            ImageData.append("Image", Image);
            ImageData.append("ID", response?.payload?.Data?.ID);
            await dispatch(ActivityImageUpload(ImageData));
          }
          navigate("/activities");
          dispatch(clearActivityDetails());
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  const handleCreateSlug = (e: any, formRenderProps: FormRenderProps) => {
    const slug = slugify(e.value);
    formRenderProps.onChange("ActivityURL", { value: slug });
  };

  // if (loading) return <LoadingPanel gridRef={gridRef} />;
  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <div>
        <Form
          onSubmit={handleSubmit}
          key={formKey}
          initialValues={ActivityDetail}
          render={(formRenderProps: FormRenderProps) => (
            <FormElement>
              <ShadowCard style={{ padding: 12 }}>
                <GridLayout
                  style={{ marginRight: 20 }}
                  gap={{ rows: 5, cols: 10 }}
                  cols={[
                    { width: "33.33%" },
                    { width: "33.33%" },
                    { width: "33.33%" },
                  ]}
                >
                  <GridLayoutItem colSpan={3}>
                    <Typography.h4>
                      {ActivityID ? "Update Activity" : "Add Activity"}
                    </Typography.h4>
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      id={"PropertyID"}
                      name={"PropertyID"}
                      label={"Property Name"}
                      placeholder="Property Name"
                      component={FormSelectionField}
                      validator={requiredValidator}
                      astrike={true}
                      options={PropertyList?.map((property: IProperty) => {
                        return {
                          value: property?.ID,
                          label: property?.PropertyName,
                        };
                      })}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={2}>
                    <Field
                      name="Title"
                      label="Title"
                      placeholder="Title"
                      onChange={(e) => handleCreateSlug(e, formRenderProps)}
                      component={FormTextField}
                      validator={min2CharValidator}
                      astrike={true}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={3}>
                    <Field
                      name="Description"
                      label="Description"
                      component={FormRichTextField}
                      // validator={requiredValidator}
                      // astrike={true}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem rowSpan={2}>
                    <Field
                      justifyContent="start"
                      name={`Image`}
                      label="Image"
                      type="file"
                      alternateName="Image"
                      accept="image/*"
                      component={FormImageUpload}
                      validator={requiredValidator}
                      astrike={true}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      wrapperClassName="right-aligned-field"
                      name="SequenceNo"
                      label="Sequence No."
                      placeholder="0"
                      format="n0"
                      component={FormNumericTextField}
                      validator={requiredValidator}
                      astrike={true}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name={`IsActive`}
                      label="Status"
                      placeholder="i.e. Active"
                      component={FormSelectionField}
                      validator={requiredValidator}
                      astrike={true}
                      options={STATUSARRAY?.map((status: any) => {
                        return {
                          value: status?.value,
                          label: status?.label,
                        };
                      })}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem
                    colSpan={2}
                    style={{
                      display: "flex",
                      justifyContent: "end",
                      alignItems: "end",
                    }}
                  >
                    <div>
                      <ButtonWithLoading
                        label={ActivityID ? "Update" : "Save"}
                        type="submit"
                        disabled={!formRenderProps.allowSubmit || loading}
                        loading={loading}
                      />
                      <Button
                        type="button"
                        fillMode={"outline"}
                        themeColor={"primary"}
                        style={{ marginLeft: 10 }}
                        onClick={() => navigate("/activities")}
                      >
                        Cancel
                      </Button>
                    </div>
                  </GridLayoutItem>
                </GridLayout>
              </ShadowCard>
            </FormElement>
          )}
        />
      </div>
    </>
  );
};

export default CreateActivity;
